"use client"

import { useEffect, useRef, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import nProgress from "nprogress"
import "nprogress/nprogress.css"
import LogoLoading from "@src/components/Logo-Loading"
import {
  startNavigationLoading,
  stopNavigationLoading,
  subscribeNavigationLoading
} from "@src/utils/navigation-loading"

nProgress.configure({ showSpinner: false, trickleSpeed: 120 })

function isModifiedEvent(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

function shouldHandleLink(anchor: HTMLAnchorElement, event: MouseEvent) {
  if (event.defaultPrevented || isModifiedEvent(event) || event.button !== 0) {
    return false
  }
  if (anchor.target && anchor.target !== "_self") return false
  if (anchor.hasAttribute("download")) return false

  const href = anchor.getAttribute("href")
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return false
  }

  try {
    const url = new URL(href, window.location.href)
    if (url.origin !== window.location.origin) return false
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
      return false
    }
    return true
  } catch {
    return false
  }
}

const SITE_MAIN_CONTENT_ID = "site-main-content"
const NAV_LOADING_CLASS = "is-nav-loading"

/**
 * Nav loading overlay only — does NOT wrap page content as a prop.
 *
 * Previously this component received `{children}` (the full page content)
 * and used useSearchParams()/usePathname(), which forces Next.js to wrap
 * the whole subtree in a Suspense boundary. Since page content was passed
 * through that boundary, it could never appear in the initial HTML — it was
 * always deferred to a client-JS-triggered reveal, delaying LCP by
 * seconds. This overlay renders independently as a sibling of the
 * server-rendered content, so the content itself streams immediately.
 */
export function NavLoadingOverlay() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ""
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)

  const toggleContentClass = (active: boolean) => {
    document.getElementById(SITE_MAIN_CONTENT_ID)?.classList.toggle(NAV_LOADING_CLASS, active)
  }

  const begin = () => {
    pendingRef.current = true
    setPending(true)
    toggleContentClass(true)
    window.scrollTo(0, 0)
    nProgress.start()
  }

  const end = () => {
    pendingRef.current = false
    setPending(false)
    toggleContentClass(false)
    nProgress.done()
  }

  useEffect(() => {
    return subscribeNavigationLoading(isPending => {
      if (isPending) begin()
      else end()
    })
  }, [])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null
      if (!anchor || !shouldHandleLink(anchor, event)) return
      begin()
    }

    document.addEventListener("click", onClick, true)
    return () => document.removeEventListener("click", onClick, true)
  }, [])

  useEffect(() => {
    if (!pendingRef.current) return
    end()
  }, [pathname, search])

  useEffect(() => {
    if (!pending) return
    const timer = window.setTimeout(() => end(), 10000)
    return () => window.clearTimeout(timer)
  }, [pending])

  if (!pending) return null

  return <LogoLoading variant="nav" />
}

/** @deprecated Prefer NavLoadingOverlay; kept for Header Suspense slot compatibility. */
export function NavigationLoading() {
  return null
}

export { startNavigationLoading, stopNavigationLoading }
